import { DecimalPipe } from '@angular/common';
import { Component, Input, computed, signal } from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule,
} from 'ng-apexcharts';

import { SeriesPoint } from '../../../types/analytics.types';

/**
 * Courbe (aires) d'une série analytics — `SeriesPoint[]`. Réutilisée par les 3
 * dashboards ; seul le `label` de la série + la devise diffèrent.
 *
 * `value` d'un `SeriesPoint` = **GMV brut** du bucket (cf. `analytics.types`),
 * `label` = libellé lisible du bucket (jour / semaine `W##` / mois). On rend le
 * montant tel quel, l'appelant fournit juste l'intitulé et la devise.
 */
@Component({
  selector: 'app-analytics-series-chart',
  standalone: true,
  imports: [NgApexchartsModule, DecimalPipe],
  template: `
    @if (points.length === 0) {
      <div class="flex h-[260px] items-center justify-center text-sm text-gray-400 dark:text-gray-500">
        {{ emptyLabel }}
      </div>
    } @else {
      <apx-chart
        [series]="series()"
        [chart]="chart"
        [colors]="colors"
        [stroke]="stroke"
        [fill]="fill"
        [grid]="grid"
        [dataLabels]="dataLabels"
        [tooltip]="tooltip"
        [xaxis]="xaxis()"
        [yaxis]="yaxis"
      />
    }
  `,
})
export class AnalyticsSeriesChartComponent {
  private readonly _points = signal<SeriesPoint[]>([]);
  private readonly _metric = signal<'value' | 'count'>('value');

  @Input({ required: true }) set points(v: SeriesPoint[]) {
    this._points.set(v ?? []);
  }
  get points(): SeriesPoint[] {
    return this._points();
  }

  /**
   * Métrique tracée : `value` (GMV du bucket, défaut — courbes CA/GMV) ou
   * `count` (effectif du bucket — ex. réservations créées dans le temps).
   */
  @Input() set metric(v: 'value' | 'count') {
    this._metric.set(v ?? 'value');
  }
  get metric(): 'value' | 'count' {
    return this._metric();
  }

  /** Nom de la série (déjà traduit par l'appelant). */
  @Input() seriesName = '';
  /** Texte affiché quand il n'y a aucun point (déjà traduit). */
  @Input() emptyLabel = '';

  readonly series = computed<ApexAxisChartSeries>(() => {
    const metric = this._metric();
    return [
      {
        name: this.seriesName,
        data: this._points().map((p) => (metric === 'count' ? p.count : p.value)),
      },
    ];
  });

  readonly xaxis = computed<ApexXAxis>(() => ({
    type: 'category',
    categories: this._points().map((p) => p.label),
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: { style: { colors: '#9CA3AF', fontSize: '11px' } },
    tooltip: { enabled: false },
  }));

  readonly chart: ApexChart = {
    fontFamily: 'Outfit, sans-serif',
    height: 260,
    type: 'area',
    toolbar: { show: false },
    zoom: { enabled: false },
  };

  readonly colors = ['#0B8C00'];
  readonly stroke: ApexStroke = { curve: 'smooth', width: 2 };
  readonly fill: ApexFill = {
    type: 'gradient',
    gradient: { opacityFrom: 0.45, opacityTo: 0 },
  };
  readonly grid: ApexGrid = {
    borderColor: 'rgba(148,163,184,0.15)',
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
  };
  readonly dataLabels: ApexDataLabels = { enabled: false };
  readonly tooltip: ApexTooltip = { enabled: true, theme: 'dark' };
  readonly yaxis: ApexYAxis = {
    labels: {
      style: { colors: ['#9CA3AF'], fontSize: '11px' },
      formatter: (v: number) => new Intl.NumberFormat('fr').format(Math.round(v)),
    },
  };
}
